/**
 * The screener builder's draft (owner, 2026-10-26; mock:
 * `docs/screener-builder-mock.html`).
 *
 * Three steps — conditions, columns, sort and name — over one draft, written
 * out once as a `ScreenerConfig` patch. Pure, so `ScreenBuilder.tsx` is layout.
 *
 * The same draft serves **New** (seeded empty) and **Edit** (seeded from the
 * screen), so the two cannot drift. Everything it writes is what the existing
 * editors write: conditions are the screen's named library plus which are
 * ticked (`applySelection`), columns go through the picker's `applyDraft`.
 */

import type {
  SavedScreenerFilter,
  ScreenerColumn,
  ScreenerConfig,
  ScreenerExpr,
  ScreenerSort,
} from "@qufolio/model";

import { applyDraft } from "./column-picker.js";
import { presetCopy, type ConditionPreset } from "./condition-presets.js";
import { applySelection, selectedFilterIds } from "./saved-filters.js";

export interface BuilderDraft {
  name: string;
  /** The screen's whole condition library — ticked or not. */
  library: SavedScreenerFilter[];
  /** Which of `library` are applied, in the order they were ticked. */
  ticked: string[];
  /** Configured columns the draft started from, plus any described here. */
  baseColumns: ScreenerColumn[];
  /** Fields shown, in grid order. */
  shown: string[];
  sort: ScreenerSort | null;
}

/** The category a described condition lands in. */
export const DESCRIBED_CATEGORY = "Described";

/** A draft seeded from a screen — Edit — or from the columns a new one starts with. */
export function draftFrom(
  config: Pick<ScreenerConfig, "columns" | "savedFilters" | "activeFilterRef" | "activeFilterRefs" | "sort">,
  name: string,
  shown: string[],
): BuilderDraft {
  return {
    name,
    library: [...(config.savedFilters ?? [])],
    ticked: selectedFilterIds(config as ScreenerConfig),
    baseColumns: [...(config.columns ?? [])],
    shown,
    sort: config.sort?.[0] ?? null,
  };
}

export function tick(draft: BuilderDraft, id: string, on: boolean): BuilderDraft {
  const has = draft.ticked.includes(id);
  if (on === has) return draft;
  return { ...draft, ticked: on ? [...draft.ticked, id] : draft.ticked.filter((t) => t !== id) };
}

/** A preset, copied into the library and ticked — the condition presets' rule. */
export function addPreset(draft: BuilderDraft, preset: ConditionPreset, id: string): BuilderDraft {
  const copy = presetCopy(preset, id);
  return { ...draft, library: [...draft.library, copy], ticked: [...draft.ticked, id] };
}

/**
 * The first name in `base`, `base 2`, `base 3`… that no screen has.
 * Case-insensitive, as a screen tab reads.
 */
export function freeName(base: string, taken: readonly string[]): string {
  const used = new Set(taken.map((t) => t.trim().toLowerCase()));
  if (!used.has(base.toLowerCase())) return base;
  let n = 2;
  while (used.has(`${base} ${n}`.toLowerCase())) n += 1;
  return `${base} ${n}`;
}

/**
 * The draft a preset screen starts (`lib/screen-presets.ts`): its conditions
 * copied into the library and ticked — alone, so only the preset's run — its
 * columns shown in its order, its sort and a free name. The library the draft
 * already had is kept, unticked, as a new screen's always is.
 */
export function startFrom(
  draft: BuilderDraft,
  preset: { name: string; columns: readonly ScreenerColumn[]; sort: ScreenerSort },
  conditions: readonly ConditionPreset[],
  mintId: () => string,
  takenNames: readonly string[] = [],
): BuilderDraft {
  const copies = conditions.map((c) => presetCopy(c, mintId()));
  return {
    name: freeName(preset.name, takenNames),
    library: [...draft.library, ...copies],
    ticked: copies.map((c) => c.id),
    baseColumns: [...preset.columns],
    shown: preset.columns.map((c) => c.field),
    sort: preset.sort,
  };
}

/** A described condition: its words as the name, under *Described*, ticked. */
export function addDescribed(
  draft: BuilderDraft,
  words: string,
  expression: ScreenerExpr,
  id: string,
): BuilderDraft {
  const entry: SavedScreenerFilter = {
    id,
    name: words.trim(),
    category: DESCRIBED_CATEGORY,
    filter: { op: "all", children: [expression] },
  };
  return { ...draft, library: [...draft.library, entry], ticked: [...draft.ticked, id] };
}

/**
 * A described column: a computed column named for its words and shown.
 *
 * `field` is `computedN` past every computed column already configured — the
 * Columns tab's own naming — so it cannot collide with one.
 */
export function addDescribedColumn(
  draft: BuilderDraft,
  words: string,
  expression: ScreenerExpr,
  source: string | undefined,
): BuilderDraft {
  const taken = new Set(draft.baseColumns.map((c) => c.field));
  let n = draft.baseColumns.filter((c) => c.computed).length + 1;
  while (taken.has(`computed${n}`)) n += 1;
  const field = `computed${n}`;
  const column: ScreenerColumn = {
    field,
    header: words.trim(),
    computed: { expression, ...(source ? { source } : {}), rollup: "recompute" },
  };
  return { ...draft, baseColumns: [...draft.baseColumns, column], shown: [...draft.shown, field] };
}

export function showColumn(draft: BuilderDraft, field: string, on: boolean): BuilderDraft {
  const has = draft.shown.includes(field);
  if (on === has) return draft;
  return { ...draft, shown: on ? [...draft.shown, field] : draft.shown.filter((f) => f !== field) };
}

/** What the builder writes: one patch, the shape every editor already emits. */
export interface BuilderResult {
  name: string;
  patch: Pick<
    ScreenerConfig,
    "columns" | "savedFilters" | "filter" | "activeFilterRef" | "activeFilterRefs" | "sort"
  >;
}

export function finish(draft: BuilderDraft, base: ScreenerConfig): BuilderResult {
  const columns = applyDraft(draft.baseColumns, draft.shown);
  // The sort only where it names a column still on the screen.
  const sort = draft.sort && draft.shown.includes(draft.sort.field) ? [draft.sort] : [];
  const selection = applySelection({ ...base, savedFilters: draft.library }, draft.ticked);
  return {
    name: draft.name.trim() || "New screen",
    patch: { columns, savedFilters: draft.library, sort, ...selection },
  };
}
